import React, { useState } from 'react';
import { toast } from 'sonner';
import { DeskButton, DeskLink, DeskModal, Field, NotifyToggle, inputStyle } from './essayShared';

const ROLES = [
  { value: 'NOTES', label: 'Notes' },
  { value: 'BRAINSTORM', label: 'Brainstorm' },
  { value: 'FEEDBACK', label: 'Feedback' },
  { value: 'OUTLINE', label: 'Outline' },
  { value: 'DRAFT', label: 'Draft' },
];

// How a parent or advisor hands a document over. They paste a link — the same
// way the student attaches theirs — and it lands on the essay marked with who
// added it. It never becomes the current draft: that stays the student's call.
export default function ShareDocButton({ ownerName, onAttach }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <DeskLink onClick={() => setOpen(true)}>+ Share a Google Doc</DeskLink>
      {open && <ShareDocModal ownerName={ownerName} onClose={() => setOpen(false)} onAttach={onAttach} />}
    </>
  );
}

function ShareDocModal({ ownerName, onClose, onAttach }) {
  const [form, setForm] = useState({ url: '', title: '', role: 'NOTES' });
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.url.trim()) return;
    setSaving(true);
    try {
      await onAttach({ ...form, notify });
      toast.success('Shared');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to share that document');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DeskModal eyebrow={`For ${ownerName}`} title="Share a document" onClose={onClose} width={520}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Field
          label="Link"
          hint="A Google Doc, a folder, anything with a link. Make sure they can open it."
        >
          <input
            autoFocus
            style={inputStyle}
            type="url"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="https://docs.google.com/…"
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 14 }}>
          <Field label="What to call it">
            <input
              style={inputStyle}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Garage-winter photos"
            />
          </Field>
          <Field label="Kind">
            <select style={inputStyle} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.55, color: 'var(--college-muted)' }}>
          This shows on the essay as added by you. {ownerName} decides which document is the current draft.
        </p>

        <div style={{ paddingTop: 16, borderTop: '1px solid var(--college-border)' }}>
          <NotifyToggle checked={notify} onChange={setNotify} who={[ownerName]} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <DeskButton onClick={onClose}>Cancel</DeskButton>
            <DeskButton variant="dark" type="submit" disabled={saving || !form.url.trim()}>
              {saving ? 'Sharing…' : 'Share it'}
            </DeskButton>
          </div>
        </div>
      </form>
    </DeskModal>
  );
}
